import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Users, Briefcase, Car, MessageSquare, MessageCircle, User, LogOut, Globe, BadgeCheck, Shield, TrendingUp, Megaphone, Wallet, HelpCircle, Sparkles, Layers, Store, Video, Bell, Download, Newspaper, Search, Home, Clapperboard, PlusCircle } from "lucide-react";
import CreateMenu from "@/components/CreateMenu";
import { FuturoChatIcon, FuturoChatWordmark } from "@/components/FuturoChatBrand";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import AdBannerRail from "@/components/AdBannerRail";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from "@/components/ui/dropdown-menu";
import NotificationBell from "@/components/NotificationBell";
import AssistantWidget, { openAssistant } from "@/components/AssistantWidget";
import MeetingInviteListener from "@/components/MeetingInviteListener";
import { PushPermissionPrompt } from "@/components/PushNotifications";
import InstallPrompt, { useInstallPrompt, IOSInstallDialog } from "@/components/InstallPrompt";
import AppLogo, { AppName } from "@/components/AppLogo";
import SponsoredAdWidget from "@/components/SponsoredAdWidget";
import { usePresenceHeartbeat } from "@/hooks/usePresence";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { available: installAvailable, install: installApp, iosOpen: installIosOpen, setIosOpen: setInstallIosOpen } = useInstallPrompt();
  const { t, lang, setLang } = useLang();
  const location = useLocation();
  const [supportEmail, setSupportEmail] = useState("suporte@pdvfuturo.com");
  const [supportWhatsapp, setSupportWhatsapp] = useState("");
  const showFloatingHelpers = location.pathname === "/profile";
  usePresenceHeartbeat(!!user);

  useEffect(() => {
    axios.get(`${API}/settings/public`).then(r => {
      if (r.data.support_email) setSupportEmail(r.data.support_email);
      setSupportWhatsapp(r.data.support_whatsapp || "");
    }).catch(() => {});
  }, []);

  // Poll unread messages count for the badge
  const [unreadMsg, setUnreadMsg] = useState(0);
  const [unreadNotif, setUnreadNotif] = useState(0);
  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/chat/unread-total`);
        setUnreadMsg(r.data?.unread || 0);
      } catch { /* silent */ }
      try {
        const n = await axios.get(`${API}/notifications?unread=true&limit=1`);
        if (typeof n.data?.unread === "number") setUnreadNotif(n.data.unread);
      } catch { /* silent */ }
    };
    load();
    const iv = setInterval(load, 8000);
    // Chat page fires this right after marking a conversation as read so the badge drops instantly
    window.addEventListener("chat:unread-refresh", load);
    return () => { clearInterval(iv); window.removeEventListener("chat:unread-refresh", load); };
  }, [user]);

  const navs = [
    { to: "/feed", icon: Home, label: "Início", key: "feed", color: "text-blue-600" },
    { to: "/reels", icon: Clapperboard, label: "Reels", key: "reels", color: "text-pink-600" },
    { to: "/mobility", icon: Car, label: t("mobility"), key: "mobility", color: "text-green-600" },
    { to: "/notifications", icon: Bell, label: "Notificações", key: "notifications", color: "text-amber-600", badge: unreadNotif },
    { to: "/profile", icon: User, label: t("profile"), key: "profile", color: "text-blue-600" },
  ];

  const langLabels = { pt: "PT-BR", en: "EN", es: "ES" };

  return (
    <div className="min-h-screen bg-background">
      <InstallPrompt />
      {/* Top Bar */}
      <header className="glass sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-2 sm:px-4 min-h-16 py-1 sm:py-0 flex flex-wrap sm:flex-nowrap items-center justify-between gap-y-1 gap-x-1 sm:gap-3">
          <Link to="/feed" className="flex items-center gap-2 shrink-0">
            <AppLogo />
            <AppName className="font-bold text-lg tracking-tight hidden lg:inline" />
          </Link>

          {/* Main nav — on phones it drops to its own full-width row under the brand; from sm+ it sits inline (icon + label) */}
          <nav className="order-3 sm:order-none w-full sm:w-auto sm:flex-1 flex items-center justify-around sm:justify-center gap-0 sm:gap-1 min-w-0" data-testid="main-nav" aria-label="Navegação principal">
            {navs.map((n) => {
              const active = n.to && location.pathname.startsWith(n.to);
              const btn = (
                <Button
                  variant={active ? "secondary" : "ghost"}
                  size="sm"
                  className={`flex flex-col lg:flex-row items-center gap-0.5 lg:gap-2 h-auto py-1 lg:py-2 rounded-2xl lg:rounded-full relative px-2 sm:px-3 ${active ? "shadow-inner" : ""}`}
                  data-testid={n.create ? "nav-create" : undefined}
                  aria-label={n.label}
                  title={n.label}
                >
                  <n.icon className={`w-7 h-7 sm:w-6 sm:h-6 lg:w-5 lg:h-5 ${active || n.create ? n.color : ""}`} />
                  <span className="text-[11px] lg:text-base leading-none lg:leading-normal font-medium whitespace-nowrap" data-testid={`nav-${n.key}-label`}>{n.label}</span>
                  {n.badge > 0 && (
                    <span data-testid={`nav-${n.key}-badge`} className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] rounded-full min-w-4 h-4 flex items-center justify-center px-1 font-bold">
                      {n.badge > 99 ? "99+" : n.badge}
                    </span>
                  )}
                </Button>
              );
              if (n.create) return <CreateMenu key={n.key} trigger={btn} />;
              return <Link key={n.key} to={n.to} data-testid={`nav-${n.key}`}>{btn}</Link>;
            })}
          </nav>

          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <Link to="/chat" data-testid="nav-chat-btn" aria-label="Futuro Chat" title="Futuro Chat"
              className={`relative h-10 pl-2 pr-2.5 rounded-full flex flex-row items-center gap-2 shrink-0 transition-colors hover:bg-cyan-500/10 ${location.pathname.startsWith("/chat") ? "bg-cyan-500/10 ring-1 ring-cyan-500/30" : ""}`}>
              <FuturoChatIcon size={24} />
              <FuturoChatWordmark className="inline text-sm lg:text-base whitespace-nowrap" testid="nav-chat-label" />
              {unreadMsg > 0 && <span data-testid="nav-chat-badge" className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] rounded-full min-w-4 h-4 flex items-center justify-center px-1 font-bold">{unreadMsg > 99 ? "99+" : unreadMsg}</span>}
            </Link>
            <CreateMenu trigger={
              <button type="button" data-testid="nav-create" aria-label="Criar" title="Criar"
                className="w-10 h-10 rounded-full flex items-center justify-center transition-colors hover:bg-primary/10 text-primary">
                <PlusCircle className="w-6 h-6" />
              </button>
            } />
            <Link to="/search" data-testid="nav-search-btn" aria-label="Pesquisar" title="Pesquisar"
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors hover:bg-muted ${location.pathname === "/search" ? "bg-primary/10 text-primary" : "text-muted-foreground"}`}>
              <Search className="w-6 h-6" />
            </Link>
            {/* Sino visível foi movido para a navegação principal (Notificações); mantemos o componente oculto só para sons/SSE */}
            <div className="hidden" aria-hidden="true"><NotificationBell /></div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1 hidden md:flex" data-testid="lang-switcher">
                  <Globe className="w-4 h-4" />
                  <span className="hidden sm:inline">{langLabels[lang]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLang("pt")}>🇧🇷 PT-BR</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLang("en")}>🇺🇸 English</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLang("es")}>🇪🇸 Español</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="rounded-full">
                  <Avatar className="w-9 h-9 ring-2 ring-primary/20">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback>{user?.name?.[0]}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild><Link to="/profile">{t("profile")}</Link></DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-work"><Link to="/work"><Briefcase className="w-4 h-4 mr-2 text-orange-500" /> {t("work")}</Link></DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-mobility"><Link to="/mobility"><Car className="w-4 h-4 mr-2 text-green-600" /> {t("mobility")}</Link></DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-chat"><Link to="/chat"><FuturoChatIcon size={12} className="mr-2" /> {t("chat")}{unreadMsg > 0 ? ` (${unreadMsg})` : ""}</Link></DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={openAssistant} data-testid="menu-assistant"><Sparkles className="w-4 h-4 mr-2" /> Assistente IA</DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-help">
                  <a href={`mailto:${supportEmail}?subject=${encodeURIComponent("Suporte FUTURO PDV")}&body=${encodeURIComponent("Descreva sua dúvida ou problema:")}`}>
                    <HelpCircle className="w-4 h-4 mr-2" /> Ajuda / Suporte
                  </a>
                </DropdownMenuItem>
                {supportWhatsapp && (
                  <DropdownMenuItem asChild data-testid="menu-whatsapp">
                    <a href={`https://wa.me/${supportWhatsapp}?text=${encodeURIComponent(`Olá! Preciso de ajuda no FUTURO PDV.${user?.name ? ` Sou ${user.name}.` : ""}`)}`} target="_blank" rel="noreferrer">
                      <MessageCircle className="w-4 h-4 mr-2 text-emerald-600" /> Falar no WhatsApp
                    </a>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild data-testid="menu-wallet"><Link to="/wallet"><Wallet className="w-4 h-4 mr-2" /> Carteira</Link></DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-driver"><Link to="/driver"><BadgeCheck className="w-4 h-4 mr-2" /> {t("driverMode")}</Link></DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-pages"><Link to="/pages"><Layers className="w-4 h-4 mr-2" /> Páginas & Grupos</Link></DropdownMenuItem>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger data-testid="menu-ads-sub"><Megaphone className="w-4 h-4 mr-2" /> Anúncios</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem asChild data-testid="menu-ads"><Link to="/ads">Boost simples</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild data-testid="menu-ads-manager"><Link to="/ads-manager">Ads Manager</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild data-testid="menu-ads-pixels"><Link to="/ads-manager/pixels">Pixels</Link></DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuItem asChild data-testid="menu-investors"><Link to="/investors"><TrendingUp className="w-4 h-4 mr-2" /> Seja sócio</Link></DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-autonomos"><Link to="/autonomos"><Sparkles className="w-4 h-4 mr-2" /> Autônomos</Link></DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-services"><Link to="/services"><Store className="w-4 h-4 mr-2" /> Marketplace</Link></DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-news"><Link to="/news"><Newspaper className="w-4 h-4 mr-2" /> Notícias</Link></DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-meetings"><Link to="/meetings"><Video className="w-4 h-4 mr-2" /> Reuniões</Link></DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-livemap"><Link to="/livemap"><Globe className="w-4 h-4 mr-2" /> Mapa ao vivo</Link></DropdownMenuItem>
                {user?.is_admin && (
                  <DropdownMenuItem asChild data-testid="menu-admin">
                    <Link to="/admin"><Shield className="w-4 h-4 mr-2" /> Admin</Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="menu-notifications"><Link to="/notifications"><Bell className="w-4 h-4 mr-2" /> Atividade</Link></DropdownMenuItem>
                {installAvailable && (
                  <DropdownMenuItem onClick={installApp} data-testid="menu-install-app">
                    <Download className="w-4 h-4 mr-2" /> Instalar app
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-destructive">
                  <LogOut className="w-4 h-4 mr-2" /> {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 2xl:px-56">
        <AdBannerRail side="left" />
        <AdBannerRail side="right" />
        <div className="mb-4" data-testid="global-sponsored-banner">
          <SponsoredAdWidget variant="banner" limit={1} rotateEvery={7000} />
        </div>
        {children}
      </main>

      {/* Floating help + AI assistant — only on /profile to avoid overlapping other pages (e.g. chat composer) */}
      {showFloatingHelpers && (
        <a
          href={`mailto:${supportEmail}?subject=${encodeURIComponent("Suporte FUTURO PDV")}&body=${encodeURIComponent("Descreva sua dúvida ou problema:")}`}
          data-testid="global-help-btn"
          title={`Ajuda: ${supportEmail}`}
          className="fixed bottom-6 right-4 lg:right-6 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-2xl shadow-primary/40 flex items-center justify-center hover:scale-105 transition-transform"
        >
          <HelpCircle className="w-6 h-6" />
        </a>
      )}
      {user && <AssistantWidget user={user} showFab={showFloatingHelpers} />}
      {user && <MeetingInviteListener />}
      {user && <PushPermissionPrompt />}
      <IOSInstallDialog open={installIosOpen} onOpenChange={setInstallIosOpen} />
    </div>
  );
}
